import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Rocket, CalendarDays, ArrowRight, ArrowLeft, Store, Globe, Smartphone,
  Sparkles, Eye, Target, Feather, Expand, Compass, Telescope,
  TrendingUp, RotateCcw, Users,
} from "lucide-react";
import { CALENDLY_DEMO_URL } from "@/constants/links";

import Navbar from "@/components/home/Navbar";
import Footer from "@/components/home/Footer";
import FinalCTA from "@/components/home/FinalCTA";
import HeroIntro from "@/components/home/HeroIntro";

import aboutHeroImg from "@/assets/wabi-sabi-lighting.jpg";
import storyImg from "@/assets/providedassets/Our Story.jpeg";
import buildImg from "@/assets/providedassets/What We Build.jpeg";
import physicalStoreImg from "@/assets/providedassets/Physical Stores.jpeg";
import ecommerceImg from "@/assets/providedassets/Mobile Shopping.jpeg";
import mobileShoppingImg from "@/assets/providedassets/E-commerce.jpeg";
import visualCommerceImg from "@/assets/providedassets/Visual Commerce.jpeg";

import comparisonCoverImg from "@/assets/providedassets/111.jpg.jpeg";
import comparisonImg1 from "@/assets/providedassets/222.jpg.jpeg";
import comparisonImg2 from "@/assets/providedassets/333.jpg.jpeg";
import comparisonImg3 from "@/assets/providedassets/444.jpg.jpeg";
import comparisonImg4 from "@/assets/providedassets/555.jpg.jpeg";
import comparisonImg5 from "@/assets/providedassets/666.jpg.jpeg";
import comparisonImg6 from "@/assets/providedassets/777.jpg.jpeg";

const COMMERCE_EVOLUTION = [
  {
    icon: Store,
    img: physicalStoreImg,
    number: "01",
    label: "Physical Stores",
    body: "Customers visited stores to see and touch products.",
  },
  {
    icon: Globe,
    img: ecommerceImg,
    number: "02",
    label: "E-commerce",
    body: "Online catalogs made shopping more accessible.",
  },
  {
    icon: Smartphone,
    img: mobileShoppingImg,
    number: "03",
    label: "Mobile Shopping",
    body: "Shopping became faster, easier, and available anywhere.",
  },
  {
    icon: Sparkles,
    img: visualCommerceImg,
    number: "04",
    label: "Visual Commerce",
    body: "Customers can visualize, personalize, and experience products before they buy.",
    highlight: true,
  },
];

const IMPACT_STATS = [
  { icon: TrendingUp, value: "94%", label: "higher purchase confidence" },
  { icon: RotateCcw, value: "38%", label: "fewer product returns" },
  { icon: Users, value: "2.5x", label: "more customer engagement" },
];

const PRINCIPLES = [
  {
    icon: Eye,
    title: "Show, Don't Tell",
    body: "People make better decisions when they can experience products instead of imagining them.",
  },
  {
    icon: Target,
    title: "Business Impact Comes First",
    body: "Every capability we build should solve a measurable business problem — increasing conversions, reducing returns, or improving customer engagement.",
  },
  {
    icon: Feather,
    title: "Simple by Design",
    body: "Advanced technology should feel effortless to use. We remove complexity so businesses can focus on serving their customers.",
  },
  {
    icon: Expand,
    title: "Built for Scale",
    body: "From local retailers to global enterprises, NEIV is designed to grow alongside our customers.",
  },
];

const WHO_WE_SERVE = [
  "Furniture & Home Décor", "Paint & Surface Finishes", "Tile & Flooring",
  "Kitchen & Bathroom Solutions", "Interior Design Studios",
  "Architects & Design Consultants", "Real Estate Developers",
  "Commercial Interior Companies", "Home Improvement Retailers",
  "Enterprise Brands",
];

const COMPARISON = [
  { before: "Static product photos", after: "Interactive AI visualization", img: comparisonImg1 },
  { before: "Customer imagination", after: "Real-world product previews", img: comparisonImg2 },
  { before: "Long decision cycles", after: "Faster purchasing decisions", img: comparisonImg3 },
  { before: "Higher product returns", after: "More confident buyers", img: comparisonImg4 },
  { before: "Limited personalization", after: "Customized customer experiences", img: comparisonImg5 },
  { before: "Manual product presentations", after: "Intelligent visual commerce", img: comparisonImg6 },
];

// Cover slide shown first, then one slide per comparison row.
const COMPARISON_SLIDES = [
  { cover: true, img: comparisonCoverImg },
  ...COMPARISON.map((row, i) => ({ ...row, index: i + 1 })),
];

function ComparisonSlider() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((i) => (i + 1) % COMPARISON_SLIDES.length);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  const goTo = (i) => setActive((i + COMPARISON_SLIDES.length) % COMPARISON_SLIDES.length);
  const slide = COMPARISON_SLIDES[active];

  return (
    <div className="mt-12 overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-xl shadow-neutral-900/[0.06] md:grid md:grid-cols-2">
      {/* Image */}
      <div className="relative aspect-[4/3] md:aspect-auto">
        {COMPARISON_SLIDES.map((s, i) => (
          <img
            key={i}
            src={s.img}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </div>

      {/* Text */}
      <div className="flex flex-col justify-center p-10 md:p-14">
        <div className="min-h-[168px]">
          {slide.cover ? (
            <>
              <p className="text-xs font-bold uppercase tracking-widest text-primary">Why NEIV</p>
              <h3 className="mt-3 font-serif-display text-2xl font-medium leading-snug text-neutral-900 md:text-3xl">
                Why businesses choose NEIV
              </h3>
              <p className="mt-3 max-w-sm text-base leading-relaxed text-neutral-500">
                See how the product experience transforms, one shift at a time.
              </p>
            </>
          ) : (
            <>
              <p className="text-xs font-bold uppercase tracking-widest text-neutral-300">
                {String(slide.index).padStart(2, "0")} / {String(COMPARISON.length).padStart(2, "0")}
              </p>
              <p className="mt-4 text-lg leading-snug text-neutral-400 line-through decoration-neutral-300">
                {slide.before}
              </p>
              <div className="my-4 h-px w-10 bg-primary/70" />
              <p className="font-serif-display text-2xl font-medium leading-snug text-neutral-900 md:text-3xl">
                {slide.after}
              </p>
            </>
          )}
        </div>

        <div className="mt-10 flex items-center gap-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goTo(active - 1)}
              aria-label="Previous"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-500 transition-colors hover:border-primary hover:text-primary"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => goTo(active + 1)}
              aria-label="Next"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-500 transition-colors hover:border-primary hover:text-primary"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            {COMPARISON_SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === active ? "w-6 bg-primary" : "w-1.5 bg-neutral-200 hover:bg-neutral-300"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <Navbar />
      <main>
        {/* Hero */}
        <section id="about" className="relative overflow-hidden bg-neutral-50 pb-16 pt-28 md:pb-20 md:pt-36">
          {/* Right-side image panel */}
          <div className="pointer-events-none absolute right-0 top-0 hidden h-full w-[45%] lg:block" aria-hidden="true">
            <img
              src={aboutHeroImg}
              alt=""
              className="h-full w-full object-cover saturate-[1.35] contrast-[1.08] brightness-[1.03]"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-50/80 via-neutral-50/10 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-neutral-50/10" />
          </div>

          <div className="relative mx-auto max-w-6xl px-4 md:px-8">
            <div className="max-w-xl">
              <HeroIntro eyebrow="About Us" heading="Building the future of visual commerce">
                <div className="mt-5 space-y-4 text-lg leading-relaxed text-neutral-500">
                  <p>For millions of customers, that question remains unanswered. They rely on imagination, static product images, or showroom visits to make important purchasing decisions.</p>
                  <p className="font-semibold text-neutral-800">At NEIV, we're changing that.</p>
                  <p>We're building an AI-powered visual commerce platform that enables businesses to help customers experience products in their own spaces before they buy.</p>
                </div>
                <div className="mt-8 rounded-2xl border border-neutral-200 bg-white/80 px-6 py-5 shadow-sm backdrop-blur-sm">
                  <p className="text-sm text-neutral-400">Every purchase begins with a question:</p>
                  <p className="mt-1 text-xl font-semibold text-neutral-800">"How will this look in my space?"</p>
                </div>
              </HeroIntro>
            </div>
          </div>
        </section>

        {/* The opportunity */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-5xl px-4 text-center md:px-8">
            <p className="text-sm font-bold uppercase tracking-widest text-primary">The Opportunity</p>
            <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
              The next evolution is visual commerce
            </h2>
            <div className="mt-12 flex flex-col items-stretch justify-center gap-4 md:flex-row md:items-center">
              {COMMERCE_EVOLUTION.map((stage, i) => {
                const Icon = stage.icon;
                return (
                  <React.Fragment key={stage.label}>
                    <div
                      className={`w-full overflow-hidden rounded-2xl border text-left transition-shadow md:max-w-[230px] ${
                        stage.highlight
                          ? "border-primary bg-primary/5 shadow-lg shadow-primary/10"
                          : "border-neutral-200 bg-white shadow-sm shadow-neutral-900/[0.03]"
                      }`}
                    >
                      <div className="relative h-28 w-full">
                        {stage.img ? (
                          <img src={stage.img} alt={stage.label} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-neutral-50">
                            <Icon className="h-8 w-8 text-neutral-300" />
                          </div>
                        )}
                        <div
                          className={`absolute -bottom-4 left-4 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white shadow-sm ${
                            stage.highlight ? "bg-primary text-white" : "bg-white text-neutral-500"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                      </div>
                      <div className="p-5 pt-6">
                        <p className={`text-xs font-bold tracking-widest ${stage.highlight ? "text-primary" : "text-neutral-300"}`}>
                          {stage.number}
                        </p>
                        <p className={`mt-1 text-sm font-semibold ${stage.highlight ? "text-primary" : "text-neutral-800"}`}>
                          {stage.label}
                        </p>
                        <p className="mt-1.5 text-xs leading-relaxed text-neutral-500">
                          {stage.body}
                        </p>
                      </div>
                    </div>
                    {i < COMMERCE_EVOLUTION.length - 1 && (
                      <ArrowRight className="mx-auto h-5 w-5 shrink-0 rotate-90 text-neutral-300 md:rotate-0" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="mx-auto mt-12 max-w-2xl space-y-4 text-lg leading-relaxed text-neutral-500">
              <p>
                Customers no longer want to imagine products — they expect to
                experience them. Whether they're choosing a sofa, selecting a
                paint color, renovating a kitchen, or designing an office,
                visualization has become an essential part of the buying journey.
              </p>
              <p className="font-semibold text-neutral-800">
                NEIV exists to power that transformation.
              </p>
            </div>
            <div className="mx-auto mt-10 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-3">
              {IMPACT_STATS.map((stat) => {
                const StatIcon = stat.icon;
                return (
                  <div
                    key={stat.label}
                    className="flex flex-col items-center gap-2 rounded-2xl border border-neutral-200 bg-neutral-50 px-6 py-6"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <StatIcon className="h-5 w-5" />
                    </div>
                    <p className="text-2xl font-bold text-neutral-900">{stat.value}</p>
                    <p className="text-sm text-neutral-500">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Our story + what we build */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 md:grid-cols-2 md:px-8">
            <div className="rounded-2xl border border-neutral-200 bg-white p-10 shadow-sm shadow-neutral-900/[0.03]">
              <img
                src={storyImg}
                alt="Mood board connecting materials and furniture to a finished room"
                className="mb-7 h-48 w-full rounded-xl object-cover"
              />
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Our Story</p>
              <h3 className="mt-3 text-2xl font-bold text-neutral-900">
                From a common challenge to a new foundation
              </h3>
              <div className="mt-4 space-y-4 leading-relaxed text-neutral-500">
                <p>
                  Businesses invest heavily in creating great products, yet
                  customers often hesitate because they can't confidently
                  visualize those products in their own environment. This gap
                  leads to abandoned purchases, extended decision cycles, and
                  unnecessary product returns.
                </p>
                <p>Rather than asking customers to imagine the result, we asked a different question:</p>
                <p className="font-semibold text-neutral-800">
                  What if every product could be experienced before it was purchased?
                </p>
                <p>That question became the foundation of NEIV.</p>
              </div>
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-white p-10 shadow-sm shadow-neutral-900/[0.03]">
              <img
                src={buildImg}
                alt="Virtual showroom app interface with AR and customization options"
                className="mb-7 h-48 w-full rounded-xl object-cover"
              />
              <p className="text-sm font-bold uppercase tracking-widest text-primary">What We Build</p>
              <h3 className="mt-3 text-2xl font-bold text-neutral-900">
                Catalogs become interactive buying experiences
              </h3>
              <div className="mt-4 space-y-4 leading-relaxed text-neutral-500">
                <p>
                  NEIV is an AI-powered platform that transforms ordinary
                  product catalogs into interactive buying experiences.
                  Businesses can enable customers to upload a room, explore
                  products in real spaces, compare different options, and make
                  purchasing decisions with greater confidence.
                </p>
                <p>
                  From furniture and flooring to paint, kitchens, lighting, and
                  décor, NEIV helps businesses create personalized experiences
                  that bridge the gap between imagination and reality.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & vision */}
        <section className="bg-neutral-900 py-24">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 md:grid-cols-2 md:px-8">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-10">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20 text-primary">
                <Compass className="h-6 w-6" />
              </div>
              <p className="mt-5 text-sm font-bold uppercase tracking-widest text-primary">Our Mission</p>
              <h3 className="mt-2 text-2xl font-bold text-white">
                Make every purchase more confident.
              </h3>
              <p className="mt-4 leading-relaxed text-white/60">
                We believe customers should never have to rely on imagination
                when making important buying decisions. Our mission is to help
                businesses replace uncertainty with clarity through intelligent
                visualization experiences.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-10">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20 text-primary">
                <Telescope className="h-6 w-6" />
              </div>
              <p className="mt-5 text-sm font-bold uppercase tracking-widest text-primary">Our Vision</p>
              <h3 className="mt-2 text-2xl font-bold text-white">
                Build the infrastructure for visual commerce.
              </h3>
              <p className="mt-4 leading-relaxed text-white/60">
                Just as payment platforms transformed digital transactions and
                cloud platforms transformed software delivery, we believe visual
                commerce will become a fundamental layer of modern retail. Our
                vision is to become the platform that powers product
                visualization for businesses around the world.
              </p>
            </div>
          </div>
        </section>

        {/* Product principles */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Our Product Principles</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                These principles shape every product decision we make
              </h2>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {PRINCIPLES.map((principle) => {
                const Icon = principle.icon;
                return (
                  <div
                    key={principle.title}
                    className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm shadow-neutral-900/[0.03]"
                  >
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 text-lg font-bold text-neutral-900">{principle.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-500">{principle.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Who we serve */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-5xl px-4 text-center md:px-8">
            <p className="text-sm font-bold uppercase tracking-widest text-primary">Who We Serve</p>
            <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
              Supporting organizations across industries
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              {WHO_WE_SERVE.map((industry) => (
                <span
                  key={industry}
                  className="cursor-default rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-base font-medium text-neutral-700 transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary"
                >
                  {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-4xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Why NEIV</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Why businesses choose NEIV
              </h2>
              <div className="mt-5 flex items-center justify-center gap-3 text-sm font-semibold">
                <span className="text-neutral-400">Traditional Product Experience</span>
                <ArrowRight className="h-4 w-4 shrink-0 text-neutral-300" />
                <span className="text-primary">With NEIV</span>
              </div>
            </div>
            <ComparisonSlider />
          </div>
        </section>

        {/* Looking ahead + join us */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-3xl px-4 text-center md:px-8">
            <p className="text-sm font-bold uppercase tracking-widest text-primary">Looking Ahead</p>
            <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
              We're building more than a visualization tool
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-neutral-500">
              Our roadmap includes expanding AI capabilities, enterprise
              integrations, developer APIs, collaborative workflows, and
              intelligent commerce experiences that help businesses connect
              products with customers more effectively.
            </p>
            <p className="mt-6 text-lg font-semibold text-neutral-800">
              Help businesses sell with confidence by helping customers buy with confidence.
            </p>

            <div className="mt-16 rounded-3xl border border-neutral-200 bg-white p-10 shadow-sm shadow-neutral-900/[0.03]">
              <h3 className="text-2xl font-bold text-neutral-900">Join us</h3>
              <p className="mx-auto mt-3 max-w-xl leading-relaxed text-neutral-500">
                Whether you're a retailer, manufacturer, designer, or
                enterprise, we're building the future of product experiences
                together. Let's transform the way people discover, evaluate,
                and purchase products.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link
                  to="/signup"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary/90 sm:w-auto"
                >
                  <Rocket className="h-4 w-4" />
                  Try for Free
                </Link>
                <a
                  href={CALENDLY_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-300 px-8 py-3.5 text-base font-semibold text-neutral-800 transition-all hover:border-neutral-900 sm:w-auto"
                >
                  <CalendarDays className="h-4 w-4" />
                  Book a Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        <FinalCTA
          title="Ready to bring your products to life?"
          subtitle="Experience how AI-powered visualization can transform your customer journey — from discovery to purchase."
          primaryLabel="Start Free Today"
          secondaryLabel={null}
        />
      </main>
      <Footer />
    </div>
  );
}
